import React from 'react';
import { useTranslation } from 'react-i18next';

// ------- Mini visuals (one per step) -------

// Step 1 — a new result lands in the record
const VizTrigger = () => (
  <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
    <div style={{ background: 'var(--bf-surface-2)', border: '1px solid var(--bf-rule)', padding: '6px 10px', fontFamily: 'var(--bf-mono)', fontSize: 9.5, color: 'var(--bf-muted)', display: 'flex', justifyContent: 'space-between' }}>
      <span>HEMOGLOBIN</span><span>13.1 g/dL</span>
    </div>
    <div style={{ background: 'var(--bf-grad-red-soft)', border: '1px solid color-mix(in oklab, var(--bf-accent) 35%, transparent)', borderLeft: '3px solid var(--bf-accent)', padding: '8px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontFamily: 'var(--bf-mono)', fontSize: 9.5, color: 'var(--bf-accent-2)' }}>NEW RESULT · TODAY</span>
      <span style={{ fontFamily: 'var(--bf-sans)', fontWeight: 600, fontSize: 12, color: 'var(--bf-accent)' }}>Hb 10.4 g/dL ↓</span>
    </div>
    <div style={{ background: 'var(--bf-surface-2)', border: '1px solid var(--bf-rule)', padding: '6px 10px', fontFamily: 'var(--bf-mono)', fontSize: 9.5, color: 'var(--bf-muted)', display: 'flex', justifyContent: 'space-between' }}>
      <span>NEUTROPHILS</span><span>4.93 ×10⁹/L</span>
    </div>
  </div>
);

// Step 2 — reads the new result against the whole patient
const VizContext = () => (
  <svg viewBox="0 0 320 160" style={{ width: '100%', height: '100%' }}>
    <g stroke="rgba(255,255,255,0.18)" strokeWidth="0.6">
      <line x1="160" y1="80" x2="50" y2="32" />
      <line x1="160" y1="80" x2="50" y2="130" />
      <line x1="160" y1="80" x2="270" y2="32" />
      <line x1="160" y1="80" x2="270" y2="130" />
      <line x1="160" y1="80" x2="160" y2="22" />
    </g>
    <g fontFamily="JetBrains Mono" fontSize="10">
      <circle cx="160" cy="80" r="18" fill="#f01f3d" />
      <text x="160" y="84" textAnchor="middle" fill="#fff" fontSize="8.5">new result</text>
      <g fill="#c9ced6">
        <circle cx="50" cy="32" r="4" fill="#14181e" stroke="#8b929c" />
        <text x="60" y="36">diagnoses</text>
        <circle cx="50" cy="130" r="4" fill="#14181e" stroke="#8b929c" />
        <text x="60" y="134">medications</text>
        <circle cx="270" cy="32" r="4" fill="#14181e" stroke="#8b929c" />
        <text x="264" y="36" textAnchor="end">past results</text>
        <circle cx="270" cy="130" r="4" fill="#14181e" stroke="#8b929c" />
        <text x="264" y="134" textAnchor="end">history</text>
        <circle cx="160" cy="22" r="4" fill="#14181e" stroke="#8b929c" />
        <text x="160" y="16" textAnchor="middle">guidelines</text>
      </g>
    </g>
  </svg>
);

// Step 3 — the interpretation appears in the EMR's own note
const VizNote = () => (
  <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
    <div style={{ background: 'var(--bf-surface-2)', border: '1px solid var(--bf-rule)', padding: '6px 10px', fontFamily: 'var(--bf-mono)', fontSize: 9.5, color: 'var(--bf-muted)' }}>
      CLINICAL NOTE
    </div>
    <div style={{ background: 'var(--bf-grad-red-soft)', border: '1px solid color-mix(in oklab, var(--bf-accent) 35%, transparent)', borderLeft: '3px solid var(--bf-accent)', padding: '10px 12px' }}>
      <div style={{ fontFamily: 'var(--bf-sans)', fontWeight: 600, fontSize: 11.5, color: 'var(--bf-ink)' }}>
        BloodFlow · ready for review
      </div>
      <div style={{ fontFamily: 'var(--bf-serif)', fontStyle: 'italic', fontSize: 12, color: 'var(--bf-ink-2)', marginTop: 3 }}>
        Cited · written into your EMR
      </div>
    </div>
    <div style={{ background: 'var(--bf-surface-2)', border: '1px solid var(--bf-rule)', padding: '6px 10px', fontFamily: 'var(--bf-mono)', fontSize: 9.5, color: 'var(--bf-muted)' }}>
      2026-01-22 · last visit
    </div>
  </div>
);

const steps = [
  {
    num: '01',
    title: 'A result comes in.',
    body: 'A new or follow-up lab result arrives in the EMR. That’s the trigger — nobody has to open a new tab or launch anything.',
    viz: <VizTrigger />,
  },
  {
    num: '02',
    title: 'BloodFlow contextualizes the whole patient.',
    body: 'It silently and automatically pulls together diagnoses, medications, past results and history, and contextualizes the new result against the entire patient state at that point in time — mapped to current clinical guidelines. The work a doctor would otherwise do by hand.',
    viz: <VizContext />,
  },
  {
    num: '03',
    title: 'A clear interpretation appears in the note.',
    body: 'BloodFlow writes a short, cited interpretation straight into the EMR’s own clinical note. The doctor reviews, edits and signs — the decision always stays with them.',
    viz: <VizNote />,
  },
];

const HowItWorks = () => {
  const { t } = useTranslation();

  return (
    <section id="how" className="bf-section">
      <div className="bf-container">
        <div className="bf-section-head">
          <span className="bf-eyebrow"><span className="dot"></span>{t('how.eyebrow', 'How it works')}</span>
          <div>
            <h2>
              {t('how.titleA', 'It contextualizes the patient, ')}
              <span className="bf-grad-text">{t('how.titleB', 'so the doctor doesn’t have to.')}</span>
            </h2>
            <p className="bf-lead" style={{ marginTop: 20, maxWidth: '54ch', color: 'var(--bf-muted)' }}>
              {t(
                'how.sub',
                'The moment a new blood test arrives, the entire patient clinical state at that point in time is already contextualized in the note — no new steps for your team.'
              )}
            </p>
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--bf-rule-2)' }}>
          {steps.map((c, i) => (
            <div
              key={i}
              className="bf-step-row"
              style={{
                display: 'grid',
                gridTemplateColumns: '160px 1.2fr 1fr',
                gap: 40,
                padding: '40px 0',
                borderBottom: i === steps.length - 1 ? '1px solid var(--bf-rule-2)' : '1px solid var(--bf-rule)',
                alignItems: 'start',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--bf-mono)',
                  fontSize: 11,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: 'var(--bf-muted)',
                  paddingTop: 6,
                }}
              >
                {c.num}
              </div>
              <div>
                <h3>{t(`how.step${c.num}.title`, c.title)}</h3>
                <p className="bf-body-p" style={{ marginTop: 12, maxWidth: '56ch' }}>
                  {t(`how.step${c.num}.body`, c.body)}
                </p>
              </div>
              <div
                style={{
                  height: 150,
                  background: 'var(--bf-bg-alt)',
                  borderRadius: 4,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {c.viz}
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .bf-step-row { grid-template-columns: 1fr !important; gap: 16px !important; }
        }
      `}</style>
    </section>
  );
};

export default HowItWorks;
